import styles from '@/styles/Shop.module.scss'
import BaseLayout from '@/layout/BaseLayout/BaseLayout'
import Head from 'next/head'
import React from 'react'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import TireSearch from '@/components/search/TireSearch/TireSearch'
import { getPageTitle } from '@/utils/helpers'
import { useTranslation } from 'next-i18next'
import { useRouter } from 'next/router'
import PageInfo from '@/components/common/PageInfo/PageInfo'
import Raw from '@/components/common/Raw/Raw'
import TyreLoadIndex from '@/components/partial/TyreLoadIndex/TyreLoadIndex'
import TyreSpeedIndex from '@/components/partial/TyreSpeedIndex/TyreSpeedIndex'
import { wrapper } from '@/store/store'
import { getCategoryHighlight, getRunningQueriesThunk, useGetCategoryHighlightQuery } from '@/api/categoryHighlights'
import GalleryViewSearchResult from '@/components/search/GalleryView/GalleryView'
import ContentSV, { meta as metaSV } from '@/content/seo/sv/dack.mdx'
import ContentEN, { meta as metaEN } from '@/content/seo/en/tyres.mdx'
import { SeoContent } from '@/lib/seo/seoPage'
import { useSeoLocaleContent } from '@/lib/seo/seoRoute'
import { useSeoLocale } from '@/lib/seo/useSeoLocale'
import { useSeoLinks } from '@/lib/seo/seoLinks'

const byLocale = {
  sv: { meta: metaSV, Content: ContentSV },
  en: { meta: metaEN, Content: ContentEN },
}

type Props = {}

const Tyres = (props: Props) => {
  const { t } = useTranslation('common')
  const router = useRouter()
  const [hasSearch, setHasSearch] = React.useState<boolean>(false)
  const locale = useSeoLocale()
  const { meta, Content } = useSeoLocaleContent(byLocale)

  const { data } = useGetCategoryHighlightQuery(10)

  return (
    <BaseLayout>
      <Head>
        <title>{getPageTitle(t('tyres:title'), locale)}</title>
        <meta name="description" content={t('tyres:description') || ''} />
        {useSeoLinks(meta)}
      </Head>
      <main>
        <PageInfo>
          <h1>{t('tyres:header')}</h1>
          <Raw content={t('tyres:pageDescription')} tagName='p' />
          <p>{t('tyres:searchDimension')}</p>
        </PageInfo>
        <TireSearch
          onHasSearch={setHasSearch}
        />

        {!hasSearch && (
          <>
            <h3 className={styles.randomArticles}>{t('tyres:randomProducts')}</h3>
            <GalleryViewSearchResult
              items={data || []}
              listId={"highlight_tyres"}
              listName={"Highlight Tyres"}
            />
          </>
        )}

        <PageInfo>
          <SeoContent demoteHeadings>
            <Content />
          </SeoContent>
        </PageInfo>

        {/* Kept from the previous copy: the load and speed rating tables are reference
            material the new content only touches briefly in /dack/faq. They were written
            for the Swedish page and are not part of the translated delivery. */}
        <PageInfo languageCode='SV'>
          <h3>Index för belastning och hastighet för däck</h3>
          <p>Att utgå efter både belastnings- och hastighetsindex när du väljer däck till din pickup, SUV eller stadsjeep är avgörande av flera anledningar:</p>
          <ul>
            <li>Säkerhet: Däck som har rätt belastnings- och hastighetsindex för din pickup kommer att kunna hantera bilens vikt och den hastighet du kör i, vilket minskar risken för däckskador och olyckor.</li>
            <li>Prestanda: Rätt däck kommer att förbättra din pickups prestanda, inklusive grepp, bromsförmåga och manövrerbarhet, vilket ger en bättre körupplevelse.</li>
            <li>Livslängd: Däck som är korrekt dimensionerade för din pickups vikt och hastighet kommer att slitas jämnt och ha en längre livslängd, vilket sparar dig pengar i det långa loppet.</li>
            <li>Komfort: Korrekt valda däck kan bidra till en mjukare och tystare körning, vilket ökar komforten för dig och dina passagerare.</li>
            <li>Bränsleeffektivitet: Däck med rätt belastnings- och hastighetsindex kan bidra till att förbättra din pickups bränsleeffektivitet, vilket sparar dig pengar på bränslekostnader.</li>
          </ul>
          <p><strong>Belastningsindexet</strong> på ett däck är en viktig indikator på däckets förmåga att bära vikt. Detta nummer, som finns angivet på däckets sidovägg, representerar den maximala vikten som däcket kan bära vid full inflation. Ett högre belastningsindex innebär att däcket kan bära mer vikt. Det är viktigt att välja däck med rätt belastningsindex för din bil och din körstil för att säkerställa optimal prestanda och säkerhet. I Sverige, liksom i resten av världen, är det viktigt att alltid konsultera din biltillverkare för rekommendationer eller oss på Statusfälgar.se innan du köper nya däck.</p>

          <TyreLoadIndex />

          <p>
            <strong>Hastighetsindexet</strong> på ett däck är en viktig faktor att beakta vid val av däck. Detta index, representerat av en bokstav på däckets sidovägg, anger den maximala hastighet som däcket kan hantera säkert. Varje bokstav motsvarar en specifik hastighet; till exempel, ett däck med hastighetsindex &quot;H&quot; är designat för att klara hastigheter upp till 210 km/h. I Sverige, liksom i resten av världen, är det viktigt att välja däck med rätt hastighetsindex för din bil och din körstil. Att köra snabbare än ditt däcks hastighetsindex kan leda till däckskador och potentiellt farliga körsituationer. Kom ihåg att alltid konsultera med oss på Statusfälgar.se eller en auktoriserad bilverkstad för rekommendationer innan du köper nya däck. Att förstå ditt däcks hastighetsindex är avgörande för säker och effektiv körning.
          </p>

          <TyreSpeedIndex />
        </PageInfo>
      </main>
    </BaseLayout>
  )
}

export default Tyres

export const getServerSideProps = wrapper.getServerSideProps(
  (store) => async (context) => {
    store.dispatch(getCategoryHighlight.initiate(10))
    await Promise.all(store.dispatch(getRunningQueriesThunk()))

    // Fetch translations for static rendering
    const translations = await serverSideTranslations(context.locale || 'en', [
      'common',
      'tyres',
      'shop',
      'footer',
    ])

    return {
      props: {
        ...translations,
      },
    }
  }
)